import { useMemo, useCallback, useLayoutEffect } from 'react';
import moment from 'moment';
import { isArray, isEmpty } from 'lodash';

import { useParseLocationQuery, useLocalStorage } from '~hooks/index';
import { formatterDate } from '~utils/formatter';
import { getRangesDate } from '~utils/dates';

import { Nullable } from '~globals/types';
import { RouteFilter } from '~services/route/types';
import { RouteStateTypes } from '~globals/types/enums';

interface RouteListQuerParamsFilters {
  dateFrom?: string;
  dateTo?: string;
  routeCode?: string;
  drivers?: string[] | string;
  vehicles?: string[] | string;
  states?: string[] | string;
}

export interface RoutesListFilters {
  dateFrom: Nullable<Date>;
  dateTo: Nullable<Date>;
  routeCode: string;
  drivers: string[];
  vehicles: string[];
  states: RouteStateTypes[];
}
interface RoutesListReturn {
  filters: RoutesListFilters;
  setFilters: (newFilters: RoutesListFilters) => void;
  clearFilters: () => void;
}

const { startDate, endDate } = getRangesDate({
  start: { operation: 'subtract', amount: 3, unit: 'days' },
  end: { operation: 'add', amount: 3, unit: 'days' },
});

export const defaultFilters: RoutesListFilters = {
  dateFrom: null,
  dateTo: null,
  routeCode: '',
  drivers: [],
  vehicles: [],
  states: [],
};

const defaultRouteStateTypesId = [
  RouteStateTypes.New,
  RouteStateTypes.Assigned,
  RouteStateTypes.Approve,
  RouteStateTypes.Rejected,
  RouteStateTypes.InProgress,
  RouteStateTypes.Finalized,
  RouteStateTypes.Expired,
];

const parseDateQueryParams = (date: string): Nullable<Date> => {
  const currentDate = new Date(date);

  if (currentDate.toString() === 'Invalid Date') return null;

  return currentDate;
};

const parseArrayQueryParams = (value: string | string[]): string[] =>
  !isArray(value) ? [value] : value;

const parseDateRequest = (date: Date): Nullable<string> => {
  const currentDate = formatterDate(date, {
    format: moment.HTML5_FMT.DATETIME_LOCAL_MS,
  });

  return currentDate || null;
};

const getFiltersByQueryParams = (
  queryParamsFilters: RouteListQuerParamsFilters,
): RoutesListFilters => ({
  dateFrom: queryParamsFilters.dateFrom
    ? parseDateQueryParams(queryParamsFilters.dateFrom)
    : null,
  dateTo: queryParamsFilters.dateTo
    ? parseDateQueryParams(queryParamsFilters.dateTo)
    : null,
  routeCode: queryParamsFilters.routeCode || '',
  drivers: queryParamsFilters.drivers
    ? parseArrayQueryParams(queryParamsFilters.drivers)
    : [],
  vehicles: queryParamsFilters.vehicles
    ? parseArrayQueryParams(queryParamsFilters.vehicles)
    : [],
  states: (queryParamsFilters.states
    ? parseArrayQueryParams(queryParamsFilters.states)
    : []) as unknown as RouteStateTypes[],
});

interface ParseRoutesListFiltersRequestParams {
  includeApiTransaction: boolean;
  filters: RoutesListFilters;
}

export const parseRoutesListFiltersRequest = ({
  includeApiTransaction,
  filters,
}: ParseRoutesListFiltersRequestParams): RouteFilter<boolean> => ({
  dateFrom: filters.dateFrom ? parseDateRequest(filters.dateFrom) : null,
  dateTo: filters.dateTo ? parseDateRequest(filters.dateTo) : null,
  routeCode: filters.routeCode || null,
  driversId: filters.drivers.length > 0 ? filters.drivers : null,
  vehiclesId: filters.vehicles.length > 0 ? filters.vehicles : null,
  routeStateTypesId:
    filters.states.length > 0 ? filters.states : defaultRouteStateTypesId,
  includeApiTransaction: includeApiTransaction,
});

export const useRoutesListFilters = (): RoutesListReturn => {
  const queryParams = useParseLocationQuery<RouteListQuerParamsFilters>();

  const queryParamsFilters = useMemo(() => {
    if (isEmpty(queryParams)) return null;

    return getFiltersByQueryParams(queryParams);
  }, [queryParams]);

  const { state: localStorageFilters, set: setLocalStorageFilters } =
    useLocalStorage<RoutesListFilters>({
      key: 'Routes-List-Filters',
      initialValue: { ...defaultFilters, dateFrom: startDate, dateTo: endDate },
    });

  useLayoutEffect(() => {
    if (queryParamsFilters) {
      setLocalStorageFilters(queryParamsFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParamsFilters]);

  const currentFilters = useMemo(
    () => queryParamsFilters ?? localStorageFilters ?? defaultFilters,
    [queryParamsFilters, localStorageFilters],
  );

  const clearFilters = useCallback(() => {
    setLocalStorageFilters(defaultFilters);
  }, [setLocalStorageFilters]);

  return {
    filters: currentFilters,
    setFilters: setLocalStorageFilters,
    clearFilters,
  };
};
